import { useTranslations } from 'next-intl';
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/use-toast';
import { MAX_PROMPT_LENGTH } from '@/app/constants';
import { cn } from '@/app/utils';

const DEFAULT_MIN_TEXTAREA_HEIGHT = 116;

const nextFrame = (callback: () => void) => {
  requestAnimationFrame(() => requestAnimationFrame(callback));
};

const getElementOuterHeight = (element: HTMLElement | null) => {
  if (!element) return 0;

  const style = window.getComputedStyle(element);
  return element.offsetHeight + Number.parseFloat(style.marginTop) + Number.parseFloat(style.marginBottom);
};

export const BasicTextarea = forwardRef<
  HTMLTextAreaElement,
  {
    textPrompt: string;
    updateTextPrompt: (value: string) => void;
    showClearButton: boolean;
    onClearPrompt: () => void;
    placeholder: string;
    extraContent?: React.ReactNode;
    className?: string;
    fitContainer?: boolean;
    minHeight?: number;
    maxHeight?: number;
    clearButtonPlacement?: 'actions' | 'inside';
    /** `false`：不设置原生 maxlength，由父组件自行限制（如按汉字加权长度） */
    maxLength?: number | false;
  }
>(
  (
    {
      textPrompt,
      updateTextPrompt,
      showClearButton,
      onClearPrompt,
      placeholder,
      extraContent,
      className,
      fitContainer = false,
      minHeight = DEFAULT_MIN_TEXTAREA_HEIGHT,
      maxHeight,
      clearButtonPlacement = 'actions',
      maxLength,
    },
    ref,
  ) => {
    const t = useTranslations();
    const limit = maxLength === false ? undefined : (maxLength ?? MAX_PROMPT_LENGTH);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const actionsRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [availableHeight, setAvailableHeight] = useState<number | null>(null);
    const [isPanelAtMaxHeight, setIsPanelAtMaxHeight] = useState(false);

    useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

    const updateTextareaHeight = () => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const effectiveMaxHeight = maxHeight ?? (fitContainer ? Number.POSITIVE_INFINITY : 320);

      textarea.style.height = 'auto';
      if (fitContainer && availableHeight) {
        const rootElement = rootRef.current;
        const contentHeight = Math.max(textarea.scrollHeight, minHeight);
        const panel = rootElement?.closest('[data-create-input-panel="true"]');
        const panelMaxHeight = panel ? Number.parseFloat(window.getComputedStyle(panel).maxHeight) : Number.NaN;
        const maxTextareaHeight =
          panel && rootElement && Number.isFinite(panelMaxHeight)
            ? Math.max(
                Math.min(
                  effectiveMaxHeight,
                  panelMaxHeight -
                    (panel.scrollHeight - rootElement.clientHeight) -
                    getElementOuterHeight(actionsRef.current),
                ),
                minHeight,
              )
            : effectiveMaxHeight;
        const targetHeight = textPrompt.length === 0 ? minHeight : contentHeight;
        textarea.style.height = `${Math.min(targetHeight, maxTextareaHeight)}px`;
        return;
      }
      textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, minHeight), effectiveMaxHeight)}px`;
    };

    useEffect(() => {
      updateTextareaHeight();
    }, [availableHeight, fitContainer, isPanelAtMaxHeight, maxHeight, minHeight, textPrompt]);

    useLayoutEffect(() => {
      if (!fitContainer || !rootRef.current) return;

      const updateAvailableHeight = () => {
        const rootHeight = rootRef.current?.clientHeight ?? 0;
        const actionsHeight = actionsRef.current?.offsetHeight ?? 0;
        setAvailableHeight(Math.max(rootHeight - actionsHeight - 8, minHeight));

        const panel = rootRef.current?.closest('[data-create-input-panel="true"]');
        if (!panel) {
          setIsPanelAtMaxHeight(false);
          return;
        }

        const maxHeight = Number.parseFloat(window.getComputedStyle(panel).maxHeight);
        setIsPanelAtMaxHeight(Number.isFinite(maxHeight) && panel.clientHeight >= maxHeight - 1);
      };

      updateAvailableHeight();
      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      const resizeObserver = new ResizeObserver(updateAvailableHeight);
      resizeObserver.observe(rootRef.current);
      const panel = rootRef.current.closest('[data-create-input-panel="true"]');
      if (panel) {
        resizeObserver.observe(panel);
      }
      if (actionsRef.current) {
        resizeObserver.observe(actionsRef.current);
      }

      return () => resizeObserver.disconnect();
    }, [fitContainer, extraContent, minHeight, showClearButton, clearButtonPlacement]);

    const handleClearClick = () => {
      onClearPrompt();
      nextFrame(updateTextareaHeight);
    };

    const clearButton = showClearButton ? (
      <BaseLineDelete className="cursor-pointer text-base" onClick={handleClearClick} />
    ) : null;

    const textarea = (
      <Textarea
        className={cn(
          'min-h-[116px] w-full resize-none overflow-auto rounded-none border-none bg-transparent p-0 text-sm text-system-white caret-system-blue02 placeholder:text-system-white36 focus-visible:ring-0 focus-visible:ring-offset-0 sm:text-sm lg:text-xs',
          fitContainer ? 'max-h-full flex-[1_0_auto]' : 'max-h-[320px]',
          clearButtonPlacement === 'inside' && showClearButton && 'pb-7',
          className,
        )}
        ref={textareaRef}
        value={textPrompt}
        placeholder={placeholder}
        maxLength={limit}
        onChange={(e) => {
          const { value } = e.target;
          if (limit !== undefined && value.length >= limit) {
            toast({
              type: 'warning',
              message: t('create.createForm.error.invalidLength', { limit }),
            });
          }
          updateTextPrompt(value);
        }}
        required
        style={{ minHeight, maxHeight }}
      />
    );

    const content = (
      <>
        {clearButtonPlacement === 'inside' ? (
          <div className={cn('relative', fitContainer && 'flex min-h-0 flex-[1_0_auto] flex-col')}>
            {textarea}
            {clearButton ? <div className="absolute right-0 bottom-0">{clearButton}</div> : null}
          </div>
        ) : (
          textarea
        )}
        <div
          ref={actionsRef}
          className={cn('mt-2 flex justify-end gap-3', clearButtonPlacement === 'inside' && !extraContent && 'hidden')}
        >
          {extraContent}
          {clearButtonPlacement === 'actions' ? clearButton : null}
        </div>
      </>
    );

    if (!fitContainer) {
      return content;
    }

    return (
      <div ref={rootRef} className="flex min-h-[116px] flex-[1_0_auto] flex-col" style={{ minHeight }}>
        {content}
      </div>
    );
  },
);

BasicTextarea.displayName = 'BasicTextarea';
